import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Plus, Settings, Clock, Users, ChevronRight } from 'lucide-react';

export default function HomePage() {
  const { user, localLogin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSession, setShowNewSession] = useState(false);
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [showTeamSettings, setShowTeamSettings] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [error, setError] = useState('');
  const [loginName, setLoginName] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    Promise.all([api.teams.list(), user ? api.reports.sessions() : Promise.resolve([])])
      .then(([t, s]) => { setTeams(t); setSessions(s); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  const doLocalLogin = async () => {
    if (!loginPassword) return;
    setLoginLoading(true);
    setLoginError('');
    try {
      await localLogin(loginPassword, loginName);
    } catch (e) {
      setLoginError(e.message || 'Incorrect password. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  if (loading) return <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}><div style={{ textAlign: 'center' }}><div style={{ fontSize: '3rem' }} className="pulse">🔄</div><p className="text-muted mt-4">Loading...</p></div></div>;

  return (
    <div className="page">
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ marginBottom: 6 }}>Retrospectives</h1>
        <p className="text-muted">Run better retros. Build better teams.</p>
      </div>

      {!user ? (
        <div className="card" style={{ maxWidth: 480, textAlign: 'center', padding: '48px 32px' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔄</div>
          <h2 style={{ marginBottom: 8 }}>Facilitator Sign In</h2>
          <p className="text-muted" style={{ marginBottom: 20 }}>Sign in to create and facilitate retro sessions. Participants can join via link without an account.</p>
          <div className="input-group" style={{ textAlign: 'left', marginBottom: 12 }}>
            <label className="input-label">Your Name</label>
            <input className="input" value={loginName} onChange={e => setLoginName(e.target.value)} placeholder="e.g. Ben" />
          </div>
          <div className="input-group" style={{ textAlign: 'left', marginBottom: 16 }}>
            <label className="input-label">Facilitator Password</label>
            <input className="input" type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && doLocalLogin()} placeholder="Enter password..." />
          </div>
          {loginError && <div className="toast toast-error" style={{ marginBottom: 12 }}>{loginError}</div>}
          <button className="btn btn-primary btn-lg w-full" onClick={doLocalLogin} disabled={loginLoading || !loginPassword}>
            {loginLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 32, alignItems: 'start' }}>
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontSize: '1.2rem' }}>Teams</h2>
              <button className="btn btn-primary btn-sm" onClick={() => setShowNewTeam(true)}><Plus size={14} /> New Team</button>
            </div>

            {teams.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                <p className="text-muted mb-4">No teams yet. Create your first team to get started.</p>
                <button className="btn btn-primary" onClick={() => setShowNewTeam(true)}><Plus size={14} /> Create Team</button>
              </div>
            ) : (
              <div className="flex-col gap-3">
                {teams.map(team => (
                  <div key={team.id} className="card flex items-center gap-4" style={{ padding: '16px 20px' }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--accent-dim)', border: '1px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 800, color: 'var(--accent)', flexShrink: 0 }}>
                      {team.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="font-bold truncate">{team.name}</div>
                      <div className="text-sm text-muted font-mono">{team.jira_project_key}{team.retro_jira_project_key ? ` · RA: ${team.retro_jira_project_key}` : ''}</div>
                    </div>
                    <button className="btn btn-ghost btn-icon" onClick={() => { setShowTeamSettings(team); }} title="Team settings"><Settings size={16} /></button>
                    <Link to={`/team/${team.id}`} className="btn btn-ghost btn-sm">📊 Dashboard</Link>
                    <button className="btn btn-primary btn-sm" onClick={() => { setSelectedTeam(team); setShowNewSession(true); }}><Plus size={14} /> Start Retro</button>
                  </div>
                ))}
              </div>
            )}

            {sessions.length > 0 && (
              <div style={{ marginTop: 40 }}>
                <div className="flex items-center justify-between mb-4">
                  <h2 style={{ fontSize: '1.2rem' }}>Recent Sessions</h2>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate('/reports')}>View all <ChevronRight size={14} /></button>
                </div>
                <div className="flex-col gap-2">
                  {sessions.slice(0, 5).map(s => (
                    <div key={s.session_id} className="card card-sm flex items-center gap-3" style={{ padding: '12px 16px' }}>
                      <Clock size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="text-sm font-bold truncate">{s.session_name}</div>
                        <div className="text-xs text-muted">{s.team_name} · {new Date(s.created_at).toLocaleDateString()}</div>
                      </div>
                      <span className={`badge ${s.status === 'active' ? 'badge-green' : 'badge-muted'}`}>{s.status}</span>
                      {s.status === 'active' && (
                        <button className="btn btn-primary btn-sm" onClick={() => navigate(`/session/${s.session_id}`)}>
                          Rejoin
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick join */}
          <QuickJoin navigate={navigate} />
        </div>
      )}

      {showNewTeam && <NewTeamModal onClose={() => setShowNewTeam(false)} onCreated={t => { setTeams(prev => [...prev, t]); setShowNewTeam(false); }} />}
      {showNewSession && selectedTeam && <NewSessionModal team={selectedTeam} onClose={() => { setShowNewSession(false); setSelectedTeam(null); }} onCreated={s => navigate(`/session/${s.id}`)} />}
      {showTeamSettings && <TeamSettingsModal team={showTeamSettings} onClose={() => setShowTeamSettings(null)} onUpdated={t => { setTeams(prev => prev.map(x => x.id === t.id ? t : x)); setShowTeamSettings(null); }} />}

      {error && <div style={{ position: 'fixed', bottom: 24, right: 24 }}><div className="toast toast-error">{error}<button className="btn btn-ghost btn-sm" onClick={() => setError('')}>✕</button></div></div>}
    </div>
  );
}

function QuickJoin({ navigate }) {
  const [url, setUrl] = useState('');
  const join = () => { const id = url.split('/').pop(); if (id) navigate(`/join/${id}`); };
  return (
    <div className="card" style={{ position: 'sticky', top: 80 }}>
      <h3 style={{ marginBottom: 12 }}>Join a Session</h3>
      <p className="text-sm text-muted mb-4">Have a link? Join without an account.</p>
      <div className="input-group">
        <label className="input-label">Session URL or ID</label>
        <input className="input" value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && join()} placeholder="Paste link..." />
      </div>
      <button className="btn btn-primary w-full" onClick={join} disabled={!url.trim()}><Users size={14} /> Join Session</button>
      <div className="divider" />
      <p className="text-xs text-muted" style={{ textAlign: 'center' }}>No account needed</p>
    </div>
  );
}

function NewTeamModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [retroKey, setRetroKey] = useState('RA');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!name || !projectKey) return;
    setLoading(true);
    try {
      const team = await api.teams.create({ name, jira_project_key: projectKey.toUpperCase(), retro_jira_project_key: retroKey.toUpperCase() || 'RA' });
      onCreated(team);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h3>Create Team</h3><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="input-group"><label className="input-label">Team Name</label><input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Backend Squad" /></div>
        <div className="input-group"><label className="input-label">Jira Project Key (Estimation)</label><input className="input" value={projectKey} onChange={e => setProjectKey(e.target.value)} placeholder="e.g. FA" /></div>
        <div className="input-group"><label className="input-label">Jira Project Key (Retro Actions)</label><input className="input" value={retroKey} onChange={e => setRetroKey(e.target.value)} placeholder="RA" /></div>
        {error && <div className="toast toast-error mb-4">{error}</div>}
        <div className="flex gap-3"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary w-full" onClick={submit} disabled={loading || !name || !projectKey}>{loading ? 'Creating...' : 'Create Team'}</button></div>
      </div>
    </div>
  );
}

function NewSessionModal({ team, onClose, onCreated }) {
  const [name, setName] = useState(`Retro — Sprint ${new Date().toLocaleDateString()}`);
  const [timer, setTimer] = useState(180);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formats, setFormats] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [sessionMode, setSessionMode] = useState('sequential');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    api.formats.list().then(d => { setFormats(d || []); if (d?.length) setSelectedFormat(d[0].id); }).catch(() => {});
    api.templates.list().then(d => setTemplates(d || [])).catch(() => {});
  }, []);

  const submit = async () => {
    setLoading(true);
    try {
      const s = await api.sessions.create({
        team_id: team.id,
        name,
        timer_seconds: parseInt(timer),
        format_id: selectedFormat || undefined,
        mode: sessionMode,
        template_id: selectedTemplate || undefined,
      });
      onCreated(s);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h3>Start Retro Session</h3><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="mb-4"><span className="badge badge-purple">{team.name}</span></div>
        <div className="input-group"><label className="input-label">Session Name</label><input className="input" value={name} onChange={e => setName(e.target.value)} /></div>
        <div className="input-group">
          <label className="input-label">Timer per phase</label>
          <select className="input" value={timer} onChange={e => setTimer(e.target.value)}>
            <option value="120">2 minutes</option><option value="180">3 minutes (default)</option>
            <option value="240">4 minutes</option><option value="300">5 minutes</option>
          </select>
        </div>
        {formats.length > 0 && (
          <div className="input-group">
            <label className="text-xs text-muted mb-1 block">Format</label>
            <select className="input mb-3" value={selectedFormat} onChange={e => setSelectedFormat(e.target.value)} style={{ fontSize: 13 }}>
              {formats.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
        )}
        <div className="input-group">
          <label className="text-xs text-muted mb-1 block">Mode</label>
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              className={`btn btn-sm ${sessionMode === 'sequential' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setSessionMode('sequential')}
            >Sequential</button>
            <button
              type="button"
              className={`btn btn-sm ${sessionMode === 'all_in_one' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setSessionMode('all_in_one')}
            >All-in-One</button>
          </div>
          <p className="text-xs text-muted mb-3" style={{ marginTop: -8 }}>
            {sessionMode === 'sequential' ? 'Walk through phases one at a time.' : 'All writing phases open simultaneously.'}
          </p>
        </div>
        {templates.length > 0 && (
          <div className="input-group">
            <label className="text-xs text-muted mb-1 block">Start from template (optional)</label>
            <select className="input mb-3" value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)} style={{ fontSize: 13 }}>
              <option value="">— No template —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        {error && <div className="toast toast-error mb-4">{error}</div>}
        <div className="flex gap-3"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary w-full" onClick={submit} disabled={loading || !name}>{loading ? 'Starting...' : 'Start Session'}</button></div>
      </div>
    </div>
  );
}

function TeamSettingsModal({ team, onClose, onUpdated }) {
  const [name, setName] = useState(team.name);
  const [jiraKey, setJiraKey] = useState(team.jira_project_key || '');
  const [retroKey, setRetroKey] = useState(team.retro_jira_project_key || 'RA');
  const [slackChannel, setSlackChannel] = useState(team.slack_channel_id || '');
  const [notionDb, setNotionDb] = useState(team.notion_database_id || '');
  const [playbookCtx, setPlaybookCtx] = useState(team.playbook_context || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setLoading(true);
    try {
      const t = await api.teams.update(team.id, { name, jira_project_key: jiraKey, retro_jira_project_key: retroKey, slack_channel_id: slackChannel, notion_database_id: notionDb, playbook_context: playbookCtx });
      onUpdated(t);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-wide">
        <div className="modal-header"><h3>Team Settings — {team.name}</h3><button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button></div>
        <div className="grid-2">
          <div className="input-group"><label className="input-label">Team Name</label><input className="input" value={name} onChange={e => setName(e.target.value)} /></div>
          <div className="input-group"><label className="input-label">Estimation Jira Project</label><input className="input" value={jiraKey} onChange={e => setJiraKey(e.target.value.toUpperCase())} /></div>
          <div className="input-group"><label className="input-label">Retro Actions Project Key</label><input className="input" value={retroKey} onChange={e => setRetroKey(e.target.value.toUpperCase())} placeholder="RA" /></div>
          <div className="input-group"><label className="input-label">Slack Channel ID</label><input className="input" value={slackChannel} onChange={e => setSlackChannel(e.target.value)} placeholder="C0XXXXXXX" /></div>
        </div>
        <div className="input-group"><label className="input-label">Notion Database ID (for future export)</label><input className="input" value={notionDb} onChange={e => setNotionDb(e.target.value)} placeholder="32-character database ID" /></div>
        <div className="input-group">
          <label className="input-label">AI Context — Team Guidelines</label>
          <textarea className="input" value={playbookCtx} onChange={e => setPlaybookCtx(e.target.value)} rows={4} placeholder="Any team-specific rules the AI should know when suggesting actions. E.g. 'This team is responsible for the mobile app. Backend changes require sign-off from the platform team.'" style={{ resize: 'vertical' }} />
        </div>
        {error && <div className="toast toast-error mb-4">{error}</div>}
        <div className="flex gap-3"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary w-full" onClick={submit} disabled={loading}>{loading ? 'Saving...' : 'Save Settings'}</button></div>
      </div>
    </div>
  );
}
